<template>
  <section class="content">
    <div class="account-element flex">
      <div class="left-content" :class="{ h500: showSelect }">
        <a-tree
          :tree-data="treeList"
          :selectedKeys.sync="selectedKeys"
          :expandedKeys="expandedKeys"
          @expand="onExpand"
          @select="onSelect"
        />
      </div>
      <div class="right-content">
        <div class="form-content pl20">
          <a-row :gutter="16" style="margin-top: 15px">
            <a-col :span="6">
              <CommonQuerySelect
                api="getAccountTableList"
                :code.sync="accountTableCode"
                :placeholder="$t('请选择科目表')"
              />
            </a-col>
            <a-col :span="6">
              <a-input
                allow-clear
                v-model.trim="nameOrCode"
                :placeholder="$t('搜索编码/名称')"
                @keydown.enter="getTableList(1)"
              />
            </a-col>
            <a-col :span="12">
              <a-button @click="getTableList(1)" type="primary" style="margin-right: 10px">
                <i class="iconfont" style="margin-right: 5px">&#xe647;</i>
                {{ $t(`查询`) }}
              </a-button>
              <a-button @click="handleAddAccount" type="primary">
                {{ $t('新增科目') }}
              </a-button>
            </a-col>
          </a-row>
        </div>
        <div class="table-content">
          <div class="table-body">
            <CommonTable
              :showSelect="showSelect"
              :columns.sync="columns"
              :data-source="tableList"
              :scroll="{ y: scrollHeight }"
              :loading="loading"
              :showPagination="false"
              :selectedRowKeys.sync="selectedRowKeys"
              :selectedRows.sync="selectedRows"
              rowKey="code"
              @select="onTableSelect"
            >
              <template v-slot:code="{ text, record }">
                <span class="text-link" @click="viewDetail(record)">{{ text }}</span>
              </template>
              <template v-slot:operation="{ record }">
                <div style="display: flex; gap: 10px">
                  <a @click="handleCreateChild(record)">{{ $t('创建科目') }}</a>
                  <a v-if="record.is_preset === 0" @click="handleEditAccount(record)">
                    {{ $t('编辑') }}
                  </a>
                  <a-popconfirm
                    v-if="record.is_preset === 0"
                    :title="$t('确认删除该科目吗？')"
                    @confirm="handleDeleteAccount(record)"
                  >
                    <a class="text-danger">{{ $t('删除') }}</a>
                  </a-popconfirm>
                </div>
              </template>
            </CommonTable>
          </div>
          <div class="table-footer">
            <Pagination
              :pageNo="pageNo"
              :length="pageSize"
              :total-count="total"
              @paginate="paginate"
            />
          </div>
        </div>
      </div>
    </div>
    <a-modal
      :title="addAccountModalTitle"
      :visible="addAccountVisible"
      :width="800"
      :confirmLoading="confirmLoading"
      @ok="handleAddAccountOk"
      @cancel="handleAddAccountCancel"
    >
      <a-form-model ref="addAccountForm" :model="addAccountForm" :rules="addAccountRules">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item :label="$t('编码')" prop="code">
              <a-input v-model.trim="addAccountForm.code" :placeholder="$t('请输入编码')" />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('名称')" prop="name">
              <a-input v-model.trim="addAccountForm.name" :placeholder="$t('请输入名称')" />
            </a-form-model-item>
          </a-col>
          <a-col v-if="parentAccountName" :span="12">
            <a-form-model-item :label="$t('上级科目')">
              <a-input :value="parentAccountName" disabled />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('科目表')" prop="accountTableCode">
              <CommonQuerySelect
                api="getAccountTableList"
                :code.sync="addAccountForm.accountTableCode"
                :name.sync="addAccountForm.accountTableName"
                :placeholder="$t('请选择科目表')"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('会计要素表')" prop="accountingElementTableCode">
              <CommonQuerySelect
                api="getAccountElemenTabletist"
                :code.sync="addAccountForm.accountingElementTableCode"
                :name.sync="addAccountForm.accountingElementTableName"
                :params="{ level: 0 }"
                :placeholder="$t('请选择会计要素表')"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('会计要素')" prop="elementCode">
              <CommonQuerySelect
                api="getAccountElementist"
                :code.sync="addAccountForm.elementCode"
                :name.sync="addAccountForm.elementName"
                :placeholder="$t('请选择会计要素')"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('余额方向')" prop="balanceDirection">
              <a-select
                v-model="addAccountForm.balanceDirection"
                :placeholder="$t('请选择余额方向')"
              >
                <a-select-option
                  v-for="item in balanceDirectionList"
                  :key="item.code"
                  :value="item.code"
                >
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item :label="$t('核算维度')">
              <a-input
                readonly
                :placeholder="$t('请选择核算维度')"
                @click="showDimensionModal"
                :value="dimensionName || undefined"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('是否现金流量项目')">
              <a-switch
                :checked-children="$t('是')"
                :un-checked-children="$t('否')"
                :checked="addAccountForm.isCashFlowItem === 1"
                @change="(checked) => (addAccountForm.isCashFlowItem = checked ? 1 : 0)"
              />
            </a-form-model-item>
          </a-col>
          <a-col v-if="addAccountForm.isCashFlowItem === 1" :span="12">
            <a-form-model-item :label="$t('现金流量科目类型')">
              <a-select
                v-model="addAccountForm.cashFlowAccountType"
                :placeholder="$t('请选择现金流量科目类型')"
                allowClear
              >
                <a-select-option
                  v-for="item in cashFlowAccountTypeList"
                  :key="item.code"
                  :value="item.code"
                >
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>
    <SelectTableModal
      ref="SelectDimensionModalRef"
      rowKey="value_code"
      api="getAccountDimensionList"
      :selecteds="dimensionSelectedRows"
      :title="$t('核算维度')"
      :columns="dimensionModalColumns"
      @ok="handleDimensionOk"
    />
  </section>
</template>
<script>
import { mapGetters, mapState } from 'vuex'
import http from '@/service/axios'
import api from '@/service/api'
import { buildTree } from '@/common'
import Pagination from '@/component/pagination'
import CommonQuerySelect from '@/component/commonQuerySelect'
import SelectTableModal from '@/component/selectTableModal'
export default {
  name: 'accountList',
  components: { Pagination, CommonQuerySelect, SelectTableModal },
  props: {
    showSelect: {
      type: Boolean,
      default: false,
    },
    hasSelected: {
      type: Array,
      default: () => [],
    },
    params: {
      type: Object,
      default: () => ({}),
    },
  },
  mounted() {
    this.getTreeList()
    this.getTableList()
  },
  data() {
    return {
      treeLoading: false,
      loading: false,
      treeList: [],
      tableList: [],
      rootCode: undefined,
      selectedKeys: ['all'],
      expandedKeys: [],
      visible: false,
      form: {
        code: '',
        name: '',
        balance_direction: '',
      },
      rules: {
        code: [{ required: true, message: this.$t('请输入编码'), trigger: 'blur' }],
        name: [{ required: true, message: this.$t('请输入名称'), trigger: 'blur' }],
        balance_direction: [
          { required: true, message: this.$t('请选择余额方向'), trigger: 'change' },
        ],
      },
      pageNo: 1,
      pageSize: 20,
      total: 1,
      selectedRowKeys: [],
      selectedRows: [],
      accountTableCode: undefined,
      nameOrCode: undefined,
      selectData: {},
      addAccountVisible: false,
      confirmLoading: false,
      addAccountForm: {
        id: undefined,
        code: '',
        name: '',
        accountTableCode: '',
        accountTableName: '',
        accountingElementTableCode: '',
        accountingElementTableName: '',
        elementCode: '',
        elementName: '',
        balanceDirection: '',
        dimensionCodeList: [],
        isCashFlowItem: 0,
        cashFlowAccountType: '',
        parentCode: '',
        rootCode: '',
      },
      addAccountRules: {
        code: [{ required: true, message: this.$t('请输入编码'), trigger: 'blur' }],
        name: [{ required: true, message: this.$t('请输入名称'), trigger: 'blur' }],
        accountTableCode: [{ required: true, message: this.$t('请选择科目表'), trigger: 'blur' }],
        accountingElementTableCode: [
          { required: true, message: this.$t('请选择会计要素表'), trigger: 'blur' },
        ],
        elementCode: [{ required: true, message: this.$t('请选择会计要素'), trigger: 'blur' }],
        balanceDirection: [{ required: true, message: this.$t('请选择余额方向'), trigger: 'blur' }],
      },
      dimensionName: '',
      dimensionSelectedRows: [],
      isEditAccount: false,
      parentAccountName: '',
    }
  },
  watch: {
    hasSelected(val) {
      if (val.length > 0) {
        this.selectedRows = val
        this.selectedRowKeys = val.map((item) => item.code)
      } else {
        this.selectedRows = []
        this.selectedRowKeys = []
      }
    },
  },
  computed: {
    ...mapGetters(['balanceDirectionMapping', 'cashFlowAccountTypeMapping']),
    ...mapState(['balance_direction', 'cash_flow_account_type']),
    addAccountModalTitle() {
      return this.isEditAccount ? this.$t('编辑科目') : this.$t('新增科目')
    },
    scrollHeight() {
      return this.showSelect ? 500 : window.innerHeight - 300
    },
    balanceDirectionList() {
      return this.balance_direction || []
    },
    cashFlowAccountTypeList() {
      return this.cash_flow_account_type || []
    },
    columns() {
      const cols = [
        {
          title: this.$t('编码'),
          dataIndex: 'code',
          scopedSlots: { customRender: 'code' },
        },
        {
          title: this.$t('名称'),
          dataIndex: 'full_name',
        },
        {
          title: this.$t('核算维度'),
          dataIndex: 'dimension_name',
          customRender: (text, record) => {
            if (record.dimension_list.length > 0) {
              return record.dimension_list.map((item) => item?.name || '').join(';')
            }
            return ''
          },
        },
        {
          title: this.$t('会计要素'),
          dataIndex: 'element_name',
        },
        {
          title: this.$t('余额方向'),
          dataIndex: 'balance_direction',
          customRender: (text) => this.balanceDirectionMapping[text],
        },
        {
          title: this.$t('上级会计要素'),
          dataIndex: 'parent_element_name',
        },
        {
          title: this.$t('现金流量项目'),
          dataIndex: 'is_cash_flow_item',
          customRender: (text) => {
            return text ? this.$t('是') : this.$t('否')
          },
        },
        {
          title: this.$t('现金流量科目类型'),
          dataIndex: 'cash_flow_account_type',
          customRender: (text) => this.cashFlowAccountTypeMapping[text] || '-',
        },
        {
          title: this.$t('系统预置'),
          dataIndex: 'is_preset',
          customRender: (text) => {
            return text ? this.$t('是') : this.$t('否')
          },
        },
        {
          title: this.$t('备注'),
          dataIndex: 'remark',
        },
      ]
      if (!this.showSelect) {
        cols.push({
          title: this.$t('操作'),
          dataIndex: 'operation',
          scopedSlots: { customRender: 'operation' },
          width: 160,
          fixed: 'right',
        })
      }
      return cols
    },
    dimensionModalColumns() {
      return [
        {
          title: this.$t('编码'),
          dataIndex: 'value_code',
        },
        {
          title: this.$t('名称'),
          dataIndex: 'name',
        },
        {
          title: this.$t('值来源'),
          dataIndex: 'value_name',
        },
      ]
    },
  },
  methods: {
    getTreeList() {
      this.treeLoading = true
      http({
        url: api.getAccountElementist,
        type: 'post',
        success: (res) => {
          this.treeLoading = false
          const tree = buildTree(res.result.list)
          this.treeList = [
            {
              title: this.$t('全部'),
              key: 'all',
              children: tree,
            },
          ]
          this.expandedKeys = ['all']
        },
      })
    },
    getTableList(isFirst = false) {
      if (isFirst) {
        this.pageNo = 1
      }
      this.loading = true
      http({
        url: api.getAccountList,
        type: 'post',
        data: {
          page_no: this.pageNo,
          page_size: this.pageSize,
          nameOrCode: this.nameOrCode,
          accountTableCode: this.accountTableCode,
          ...this.params,
          ...this.selectData,
        },
        success: (res) => {
          this.loading = false
          this.tableList = res.result.list
          this.total = res.result.total
        },
      })
    },
    onSelect(selectedKeys) {
      this.pageNo = 1
      const data = {}
      if (selectedKeys.length) {
        if (selectedKeys[0] !== 'all') {
          let arr = selectedKeys[0].split('-')
          if (arr.length === 1) {
            let selectItem = this.treeList[0].children.find((elem) => elem.code === arr[0])
            data.elementCodeList =
              selectItem.childrenCodeList?.length > 0 ? selectItem.childrenCodeList : arr
          } else {
            data.elementCodeList = [arr[1]]
          }
        }
        this.selectData = data
        this.getTableList()
      }
    },
    viewDetail(record) {
      if (this.showSelect) {
        this.$emit('change', record)
        return
      }
      this.$router.push({
        name: 'accountDetail',
        query: { id: record.id },
      })
    },
    onExpand(expandedKeys) {
      this.expandedKeys = expandedKeys
    },
    paginate(data) {
      this.pageNo = data.pageNo
      this.pageSize = data.pageSize
      this.getTableList()
    },
    onTableSelect(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.$emit('select', selectedRows)
    },
    handleAddAccount(record) {
      this.isEditAccount = false
      this.addAccountVisible = true
      this.resetAddAccountForm()
      if (record) {
        this.addAccountForm.parentCode = record.code
        this.addAccountForm.rootCode = this.getRootCode(record)
        this.parentAccountName = record.full_name || record.name
      } else {
        this.parentAccountName = ''
      }
    },
    handleAddAccountCancel() {
      this.addAccountVisible = false
      this.resetAddAccountForm()
    },
    resetAddAccountForm() {
      this.addAccountForm = {
        id: undefined,
        code: '',
        name: '',
        accountTableCode: '',
        accountTableName: '',
        accountingElementTableCode: '',
        accountingElementTableName: '',
        elementCode: '',
        elementName: '',
        balanceDirection: '',
        dimensionCodeList: [],
        isCashFlowItem: 0,
        cashFlowAccountType: '',
        parentCode: '',
        rootCode: '',
      }
      this.dimensionSelectedRows = []
      this.dimensionName = ''
      this.parentAccountName = ''
      this.$nextTick(() => {
        if (this.$refs.addAccountForm) {
          this.$refs.addAccountForm.clearValidate()
        }
      })
    },
    handleAddAccountOk() {
      this.$refs.addAccountForm.validate((valid) => {
        if (valid) {
          this.confirmLoading = true
          // 确保 dimensionCodeList 是数组
          let dimensionCodeList = []
          if (Array.isArray(this.addAccountForm.dimensionCodeList)) {
            dimensionCodeList = this.addAccountForm.dimensionCodeList
          } else if (this.addAccountForm.dimensionCodeList) {
            dimensionCodeList = [this.addAccountForm.dimensionCodeList]
          }
          // 计算 level 和 rootCode
          let level = 1
          let rootCode = this.addAccountForm.code
          if (this.addAccountForm.parentCode) {
            // 有父级：从 tableList 中查找父级记录
            const parentRecord = this.tableList.find(
              (item) => item.code === this.addAccountForm.parentCode
            )
            if (parentRecord) {
              // level = 父级 level + 1
              level = (parentRecord.level || 1) + 1
              // rootCode = 父级的 rootCode
              rootCode =
                parentRecord.root_code || parentRecord.rootCode || this.getRootCode(parentRecord)
            }
          }
          const submitData = {
            id: this.addAccountForm.id || undefined,
            accountTableCode: this.addAccountForm.accountTableCode,
            accountTableName: this.addAccountForm.accountTableName,
            accountingElementTableCode: this.addAccountForm.accountingElementTableCode,
            accountingElementTableName: this.addAccountForm.accountingElementTableName,
            balanceDirection: this.addAccountForm.balanceDirection,
            code: this.addAccountForm.code,
            dimensionCodeList: dimensionCodeList,
            elementCode: this.addAccountForm.elementCode,
            fullName: this.addAccountForm.name,
            isCashFlowItem: this.addAccountForm.isCashFlowItem,
            cashFlowAccountType: this.addAccountForm.cashFlowAccountType || undefined,
            isPreset: 0,
            level: level,
            name: this.addAccountForm.name,
            nameEn: '',
            parentCode: this.addAccountForm.parentCode,
            remark: '',
            rootCode: rootCode,
            status: 1,
          }
          http({
            url: api.createOrUpdateAccount,
            type: 'post',
            data: submitData,
            success: (res) => {
              this.confirmLoading = false
              if (res.success) {
                this.$message.success(
                  this.isEditAccount ? this.$t('编辑成功') : this.$t('新增成功')
                )
                this.addAccountVisible = false
                this.resetAddAccountForm()
                this.getTableList()
              } else {
                this.$message.error(res.msg || this.$t('新增失败'))
              }
            },
            fail: (err) => {
              this.confirmLoading = false
              this.$message.error(err?.msg || this.$t('新增失败'))
            },
          })
        }
      })
    },
    showDimensionModal() {
      this.$refs.SelectDimensionModalRef.open()
    },
    handleDimensionOk(list) {
      this.dimensionSelectedRows = list
      this.addAccountForm.dimensionCodeList = list.map((item) => item.code)
      this.dimensionName = list.map((item) => item.name).join(';')
    },
    handleCreateChild(record) {
      this.handleAddAccount(record)
    },
    handleEditAccount(record) {
      if (!record) return
      this.isEditAccount = true
      this.addAccountVisible = true
      this.resetAddAccountForm()
      const getField = (keyA, keyB) => record[keyA] ?? record[keyB] ?? ''
      this.addAccountForm.id = record.id
      this.addAccountForm.code = record.code
      this.addAccountForm.name = record.name || record.full_name
      this.addAccountForm.accountTableCode = getField('account_table_code', 'accountTableCode')
      this.addAccountForm.accountTableName = getField('account_table_name', 'accountTableName')
      this.addAccountForm.accountingElementTableCode = getField(
        'accounting_element_table_code',
        'accountingElementTableCode'
      )
      this.addAccountForm.accountingElementTableName = getField(
        'accounting_element_table_name',
        'accountingElementTableName'
      )
      this.addAccountForm.elementCode = getField('element_code', 'elementCode')
      this.addAccountForm.elementName = getField('element_name', 'elementName')
      this.addAccountForm.balanceDirection =
        record.balance_direction || record.balanceDirection || ''
      this.addAccountForm.parentCode = getField('parent_code', 'parentCode')
      this.addAccountForm.rootCode = this.getRootCode(record)
      this.addAccountForm.isCashFlowItem = record.is_cash_flow_item || record.isCashFlowItem || 0
      this.addAccountForm.cashFlowAccountType = getField(
        'cash_flow_account_type',
        'cashFlowAccountType'
      )
      this.parentAccountName = getField('parent_name', 'parentName')
      const dimensionList = record.dimension_list || []
      this.dimensionSelectedRows = dimensionList.map((item) => ({
        name: item.name,
        value_code: item.value_code || item.accounting_dimension_value_code || item.code,
        code: item.value_code || item.accounting_dimension_value_code || item.code,
      }))
      this.addAccountForm.dimensionCodeList = this.dimensionSelectedRows.map(
        (item) => item.value_code || item.code
      )
      this.dimensionName = dimensionList.map((item) => item.name).join(';')
    },
    getRootCode(record, depth = 0) {
      if (!record) return ''
      const rootCode = record.root_code || record.rootCode
      if (rootCode) return rootCode
      const parentCode = record.parent_code || record.parentCode
      if (!parentCode || depth > 10) {
        return record.code
      }
      const parent = this.tableList.find((item) => item.code === parentCode)
      if (!parent) {
        return parentCode
      }
      return this.getRootCode(parent, depth + 1)
    },
    handleDeleteAccount(record) {
      if (!record?.id) return
      http({
        url: api.deleteAccount,
        type: 'post',
        data: {
          id: record.id,
        },
        success: (res) => {
          if (res.success) {
            this.$message.success(this.$t('删除成功'))
            this.getTableList()
          } else {
            this.$message.error(res.msg || this.$t('删除失败'))
          }
        },
      })
    },
  },
}
</script>

<style scoped lang="scss">
.left-content {
  min-width: 200px;
  width: 250px;
  border-right: 1px solid #e8e8e8;
  height: calc(100vh - 180px);
  &.h500 {
    height: 500px;
  }
}
.table-content {
  flex: 1;
}
</style>
